import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Loader2, Users, Radio, MapPin, Link2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function LiveCarMeetDialog({ open, onOpenChange, coords, onCreated, onRequestPickCoords }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [convoyId, setConvoyId] = useState('');
  const [myConvoys, setMyConvoys] = useState([]);
  const [mode, setMode] = useState('existing'); // 'existing' | 'create'
  const [newConvoyName, setNewConvoyName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user || user === false) return;
    api.get('/convoys').then(({ data }) => {
      const mine = (data || []).filter((c) => c.host_id === user.id || (c.members || []).some((m) => m.user_id === user.id));
      setMyConvoys(mine);
      if (mine.length === 0) {
        setMode('create');
      } else {
        setMode('existing');
        if (mine.length === 1) setConvoyId(mine[0].id);
      }
    }).catch(() => {});
  }, [open, user]);

  if (!user || user === false) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!coords) { toast.message(t('livemeet.click_first')); return; }
    setLoading(true);
    try {
      let targetConvoyId = convoyId;
      // Step 1: if user picked "create", create the convoy first
      if (mode === 'create') {
        if (!newConvoyName.trim()) {
          toast.error(t('livemeet.new_convoy_name'));
          setLoading(false);
          return;
        }
        const { data: newConvoy } = await api.post('/convoys', {
          name: newConvoyName.trim(),
          description: '',
          season: 'summer',
          max_size: 12,
        });
        targetConvoyId = newConvoy.id;
        toast.success(t('livemeet.new_convoy_created'));
      }
      if (!targetConvoyId) {
        toast.error(t('livemeet.choose_convoy'));
        setLoading(false);
        return;
      }
      // Step 2: create the live meet linked to that convoy
      const { data } = await api.post('/live-meets', {
        title: title.trim() || t('livemeet.default_title'),
        x: coords.x,
        y: coords.y,
        game_id: 'fh6',
        convoy_id: targetConvoyId,
        spot_id: coords.spot_id || null,
      });
      toast.success(t('livemeet.created'));
      onCreated?.(data);
      setTitle('');
      setConvoyId('');
      setNewConvoyName('');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-md" data-testid="livemeet-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter inline-flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#00F0FF] animate-pulse" /> {t('livemeet.title')}
          </DialogTitle>
          <DialogDescription className="text-white/60">{t('livemeet.desc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-white/80">{t('livemeet.field.title')}</Label>
            <Input
              data-testid="livemeet-title"
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('livemeet.placeholder')}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
          </div>

          {/* Convoy linkage */}
          <div>
            <Label className="text-white/80 inline-flex items-center gap-1">
              <Link2 className="w-3 h-3" /> {t('livemeet.mode.choice')} <span className="text-[#FF007F]">*</span>
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="livemeet-mode-existing"
                onClick={() => setMode('existing')}
                disabled={myConvoys.length === 0}
                className={`px-3 py-2 rounded-xl border text-xs uppercase tracking-wider font-bold transition ${
                  mode === 'existing'
                    ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]'
                    : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                <Link2 className="w-3 h-3 inline mr-1" /> {t('livemeet.mode.existing')}
              </button>
              <button
                type="button"
                data-testid="livemeet-mode-create"
                onClick={() => setMode('create')}
                className={`px-3 py-2 rounded-xl border text-xs uppercase tracking-wider font-bold transition ${
                  mode === 'create'
                    ? 'border-[#FF007F] bg-[#FF007F]/10 text-[#FF007F]'
                    : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                <Plus className="w-3 h-3 inline mr-1" /> {t('livemeet.mode.create')}
              </button>
            </div>

            {mode === 'existing' && (
              <div className="mt-3">
                <select
                  data-testid="livemeet-convoy-select"
                  value={convoyId}
                  onChange={(e) => setConvoyId(e.target.value)}
                  required
                  className="w-full bg-black/40 border border-white/10 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-[#00F0FF]"
                >
                  <option value="">— {t('livemeet.choose_convoy')} —</option>
                  {myConvoys.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.members_count}/{c.max_size}){c.host_id === user.id ? ' · hôte' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-white/40 mt-1">{t('livemeet.convoy_hint')}</p>
              </div>
            )}

            {mode === 'create' && (
              <div className="mt-3">
                <Input
                  data-testid="livemeet-new-convoy-name"
                  value={newConvoyName}
                  maxLength={80}
                  onChange={(e) => setNewConvoyName(e.target.value)}
                  placeholder={t('livemeet.new_convoy_placeholder')}
                  required
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
                />
                <p className="text-[10px] text-white/40 mt-1">{t('livemeet.convoy_hint')}</p>
              </div>
            )}
          </div>

          <div className="glass rounded-xl p-3 text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FF007F]" />
            {coords ? (
              <span data-testid="livemeet-coords-ready">
                {coords.spot_id ? (t('livemeet.locked_on_spot') || 'Position du spot sélectionné') : t('livemeet.coords_set')}{' '}
                <span className="font-mono text-white/50">({Math.round(coords.x)}, {Math.round(coords.y)})</span>
              </span>
            ) : (
              <button
                type="button"
                data-testid="livemeet-pick-coords"
                onClick={() => { onOpenChange(false); onRequestPickCoords?.(); }}
                className="text-[#00F0FF] hover:underline text-left"
              >
                {t('livemeet.pick_on_map')}
              </button>
            )}
          </div>
          <div className="text-[11px] text-white/40">
            <Users className="w-3 h-3 inline mr-1" /> {t('livemeet.ttl_hint')}
          </div>
          <Button
            type="submit"
            data-testid="livemeet-submit"
            disabled={
              loading ||
              !coords ||
              (mode === 'existing' && !convoyId) ||
              (mode === 'create' && !newConvoyName.trim())
            }
            className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold uppercase tracking-wide rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('livemeet.broadcast')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
